import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import useRedirect from 'utils/hooks/useRedirect';
import useSelectedInstitution from 'utils/hooks/institutions/useSelectedInstitution';
import { notifySuccess } from 'utils/notifications';

import { toggleMainUserModal } from 'store/entities/users';
import { getUserDetails } from 'store/entities/auth/thunks';
import { updateMainUser } from 'store/entities/users/thunks';

import Button from 'components/UIComponents/Button';
import ValidationError from 'components/FormControls/ValidationError';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import Modal, { ModalCard } from 'components/UIComponents/Modal';
import { Field, Label, Select, Control } from 'components/UIComponents/Form';

export default props => {
  const dispatch = useDispatch();
  const redirect = useRedirect();
  const { t } = useTranslation();
  const { institutionId } = useSelectedInstitution();

  const { users, updateMainUserSubmitting, mainUserModalOpen } = useSelector(
    state => state.users
  );
  const { userDetails } = useSelector(state => state.auth);

  const { handleSubmit, control, errors } = useForm({
    mode: 'onChange',
  });

  const userOptions = useMemo(
    () =>
      users
        .filter(user => !user?.publicUserDto?.id !== userDetails.userId)
        .map(user => ({
          value: user,
          label: `${user?.publicUserDto?.firstName} ${user?.publicUserDto?.lastName}`,
        })),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );

  const submitMainUser = async ({ mainUser }) => {
    const newMainUser = mainUser?.value;
    if (newMainUser) {
      const updateResult = await dispatch(
        updateMainUser({ institutionId, userId: newMainUser.publicUserDto.id })
      );

      if (updateMainUser.fulfilled.match(updateResult)) {
        notifySuccess(t('datagate.external.users.message.updateMainUser'));
        dispatch(toggleMainUserModal())
        dispatch(getUserDetails());
        redirect('');
      }
    }
  };

  return (
    <Modal
      show={mainUserModalOpen}
      onClose={() => dispatch(toggleMainUserModal())}
      closeOnBlur={!updateMainUserSubmitting}
      {...props}
    >
      <ModalCard>
        <form onSubmit={handleSubmit(submitMainUser)}>
          <ModalCard.Head>
            <ModalCard.Title>
              {t('datagate.external.users.modal.changeMainUser.title')}
            </ModalCard.Title>
          </ModalCard.Head>
          <ModalCard.Body>
            <Section className="vertical-padding-md">
              <Row>
                <Column size={12} className="vertical-padding-none">
                  <Field>
                    <Control>
                      <Label required htmlFor="mainUser">
                        {t(
                          'datagate.external.users.modal.changeMainUser.form.field.label'
                        )}
                      </Label>
                      <Controller
                        name="mainUser"
                        control={control}
                        as={
                          <Select
                            inputId="mainUser"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: base => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        }
                        defaultValue={null}
                        placeholder={t(
                          'datagate.external.users.modal.changeMainUser.form.field.placeholder'
                        )}
                        options={userOptions}
                        rules={{
                          required: t(
                            'datagate.external.users.modal.changeMainUser.form.requiredMessage'
                          ),
                        }}
                      />
                    </Control>
                    <ValidationError name="mainUser" errors={errors} />
                  </Field>
                  <p>
                    {t('datagate.external.users.modal.changeMainUser.info')}
                  </p>
                </Column>
              </Row>
            </Section>
          </ModalCard.Body>
          <ModalCard.Foot>
            <Button
              type="button"
              color="primary"
              onClick={() => dispatch(toggleMainUserModal())}
              outlined
              id="abandonmainUserChangeButton"
              disabled={updateMainUserSubmitting}
            >
              &#215; {t('datagate.external.global.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={updateMainUserSubmitting}
              id="confirmMainUserChangeButton"
            >
              {t('datagate.external.global.confirm')}
            </Button>
          </ModalCard.Foot>
        </form>
      </ModalCard>
    </Modal>
  );
};
